import { PrayerTimes } from "../types/PrayerTypes";
import { alignTimetablesToToday } from "./alignTimetablesToToday";

interface UpcomingSalahChangesEligibilityParams {
  timetables: PrayerTimes[];
  prayerTimes?: PrayerTimes | null;
  prevFridayJummah1?: string | null;
  prevFridayJummah2?: string | null;
  prevFridayJummah3?: string | null;
  prevFridayJummah4?: string | null;
  baseDate?: Date;
}

function isValidTime(value?: string | null): value is string {
  return !!value && value !== "--:--";
}

export function hasUpcomingSalahChanges({
  timetables: rawTimetables,
  prayerTimes,
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
  baseDate,
}: UpcomingSalahChangesEligibilityParams): boolean {
  const today = baseDate ? new Date(baseDate) : new Date();
  const timetables = alignTimetablesToToday(rawTimetables, today);

  if (!timetables || timetables.length < 1) {
    return false;
  }

  const isFriday = (date: Date): boolean => date.getDay() === 5;

  const prayerKeys: Array<keyof PrayerTimes> = [
    "JamFajr",
    "JamDhuhr",
    "JamAsar",
    "JamEsha",
  ];

  for (const key of prayerKeys) {
    if (key === "JamDhuhr") {
      let lastNonFridayTime: string | undefined;
      let lastNonFridayIndex = -1;

      for (let i = 0; i < timetables.length; i++) {
        const checkDate = new Date(today);
        checkDate.setDate(today.getDate() + i);

        const time = timetables[i][key] as string | undefined;
        if (!isFriday(checkDate) && isValidTime(time)) {
          lastNonFridayTime = time;
          lastNonFridayIndex = i;
          break;
        }
      }

      if (!lastNonFridayTime) {
        continue;
      }

      for (let i = lastNonFridayIndex + 1; i < timetables.length; i++) {
        const changeDate = new Date(today);
        changeDate.setDate(today.getDate() + i);

        if (isFriday(changeDate)) {
          continue;
        }

        const nextDayTime = timetables[i][key] as string | undefined;

        if (
          changeDate.getDay() === 6 &&
          i > 0 &&
          timetables[i][key] === timetables[i - 1][key] &&
          isFriday(
            new Date(
              today.getFullYear(),
              today.getMonth(),
              today.getDate() + i - 1,
            ),
          )
        ) {
          continue;
        }

        if (!isValidTime(nextDayTime) || nextDayTime === lastNonFridayTime) {
          continue;
        }

        return true;
      }

      continue;
    }

    const referenceTime = timetables[0][key] as string | undefined;
    if (!isValidTime(referenceTime)) {
      continue;
    }

    for (let i = 1; i < timetables.length; i++) {
      const nextDayTime = timetables[i][key] as string | undefined;
      if (!isValidTime(nextDayTime) || nextDayTime === referenceTime) {
        continue;
      }

      return true;
    }
  }

  if (prayerTimes) {
    const jummahPairs: Array<[string | undefined, string | null | undefined]> =
      [
        [prayerTimes.Jummah1, prevFridayJummah1],
        [prayerTimes.Jummah2, prevFridayJummah2],
        [prayerTimes.Jummah3, prevFridayJummah3],
        [prayerTimes.Jummah4, prevFridayJummah4],
      ];

    for (const [current, previous] of jummahPairs) {
      if (
        isValidTime(current) &&
        isValidTime(previous) &&
        current !== previous
      ) {
        return true;
      }
    }
  }

  return false;
}
